import React, { useState, useEffect } from 'react';
import { adminAPI, adminAssessmentAPI } from '../services/api';
import toast from 'react-hot-toast';

const CandidateSkillAssignment = () => {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateSkills, setCandidateSkills] = useState([]); // Skills from resume extraction
  const [availableSkills, setAvailableSkills] = useState([]); // All skills in the system
  const [selectedSkill, setSelectedSkill] = useState(''); // Single skill selected
  const [selectedCandidates, setSelectedCandidates] = useState([]); // Candidates selected for assignment
  const [loading, setLoading] = useState(true);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    fetchCandidates();
    fetchAllAvailableSkills();
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (selectedCandidate) {
      fetchCandidateSkills();
      setSelectedSkill(''); // Reset selected skill when candidate changes
    }
  }, [selectedCandidate]);

  // When a skill is selected, auto-check candidates who already have it
  useEffect(() => {
    if (selectedSkill && candidates.length > 0) {
      checkCandidatesWithSkill(selectedSkill);
    } else if (!selectedSkill) {
      setSelectedCandidates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSkill, candidates.length]);

  // Get "Others" category skills
  const getOthersCategorySkills = () => {
    return availableSkills.filter(skill => {
      const category = (typeof skill === 'object' && skill.category) ? skill.category : 'Uncategorized';
      return category === 'Others';
    });
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCandidates(currentPage, pageSize, searchTerm);
      setCandidates(response.data.candidates || response.data);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAvailableSkills = async () => {
    try {
      setLoadingSkills(true);
      // Get skills from skills overview endpoint (same way as AdminAssessmentManagement)
      const overviewResponse = await adminAssessmentAPI.getSkillsOverview();
      
      let skills = [];
      // Check the response structure - it should have data.skills
      if (overviewResponse.data && overviewResponse.data.skills) {
        const skillsData = overviewResponse.data.skills;
        // Extract skills with category information
        skills = skillsData.map(skill => {
          // Handle different possible structures
          if (typeof skill === 'string') {
            return { name: skill, category: 'Uncategorized' };
          } else if (skill.skill) {
            const skillName = typeof skill.skill === 'string' ? skill.skill : skill.skill.name || skill.skill.skillName;
            return { name: skillName, category: skill.category || 'Uncategorized' };
          } else if (skill.skillName) {
            return { name: skill.skillName, category: skill.category || 'Uncategorized' };
          } else if (skill.name) {
            return { name: skill.name, category: skill.category || 'Uncategorized' };
          } else if (skill.skill_name) {
            // This is the structure from skills-overview endpoint
            return { 
              name: skill.skill_name, 
              category: skill.category || 'Uncategorized',
              skill_id: skill.skill_id,
              is_active: skill.is_active !== false
            };
          }
          return null;
        }).filter(Boolean);
      } else if (overviewResponse.data && Array.isArray(overviewResponse.data)) {
        // Fallback: if data is directly an array
        skills = overviewResponse.data.map(item => {
          if (typeof item === 'string') {
            return { name: item, category: 'Uncategorized' };
          } else if (item.skill) {
            const skillName = typeof item.skill === 'string' ? item.skill : item.skill.name || item.skill.skillName;
            return { name: skillName, category: item.category || 'Uncategorized' };
          } else if (item.skillName) {
            return { name: item.skillName, category: item.category || 'Uncategorized' };
          } else if (item.name) {
            return { name: item.name, category: item.category || 'Uncategorized' };
          } else if (item.skill_name) {
            return { 
              name: item.skill_name, 
              category: item.category || 'Uncategorized',
              skill_id: item.skill_id,
              is_active: item.is_active !== false
            };
          }
          return null;
        }).filter(Boolean);
      }
      
      // Remove duplicates based on skill name (case-insensitive)
      const uniqueSkillsMap = new Map();
      skills.forEach(skill => {
        const skillName = typeof skill === 'string' ? skill : skill.name;
        const normalizedName = skillName.toLowerCase().trim();
        if (!uniqueSkillsMap.has(normalizedName)) {
          uniqueSkillsMap.set(normalizedName, typeof skill === 'string' ? { name: skill, category: 'Uncategorized' } : skill);
        }
      });
      const uniqueSkills = Array.from(uniqueSkillsMap.values());
      
      console.log('Fetched available skills from overview:', uniqueSkills, 'Raw response:', overviewResponse.data);
      setAvailableSkills(uniqueSkills);
    } catch (error) {
      console.error('Error fetching available skills from overview:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load available skills');
      setAvailableSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  };

  const fetchCandidateSkills = async () => {
    if (!selectedCandidate) return;
    
    try {
      setLoadingSkills(true);
      // Fetch candidate profile to get current skills (from resume extraction)
      // Use candidate.id (not userId) as the endpoint expects Candidate.id
      const candidateId = selectedCandidate.id;
      console.log('Fetching candidate profile for ID:', candidateId, 'Candidate object:', selectedCandidate);
      const response = await adminAPI.getCandidateProfile(candidateId);
      const profile = response.data;
      
      // Extract skills from profile - check multiple possible locations
      let skills = [];
      
      // Check direct skills field
      if (profile.skills) {
        if (Array.isArray(profile.skills)) {
          skills = profile.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
        } else if (typeof profile.skills === 'string') {
          skills = profile.skills.split(',').map(s => s.trim()).filter(s => s);
        }
      }
      
      // Check resumeExtractedData field
      if (profile.resumeExtractedData) {
        const extractedData = profile.resumeExtractedData;
        if (extractedData.skills) {
          let extractedSkills = [];
          if (Array.isArray(extractedData.skills)) {
            extractedSkills = extractedData.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
          } else if (typeof extractedData.skills === 'string') {
            extractedSkills = extractedData.skills.split(',').map(s => s.trim()).filter(s => s);
          }
          // Merge with existing skills, avoiding duplicates
          extractedSkills.forEach(skill => {
            if (!skills.some(s => s.toLowerCase() === skill.toLowerCase())) {
              skills.push(skill);
            }
          });
        }
      }
      
      // Check if skills are in the candidate object directly (from list endpoint)
      if (skills.length === 0 && selectedCandidate.skills) {
        if (Array.isArray(selectedCandidate.skills)) {
          skills = selectedCandidate.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
        } else if (typeof selectedCandidate.skills === 'string') {
          skills = selectedCandidate.skills.split(',').map(s => s.trim()).filter(s => s);
        }
      }
      
      console.log('Fetched candidate skills:', skills, 'from profile:', profile);
      setCandidateSkills(skills);
    } catch (error) {
      console.error('Error fetching candidate skills:', error);
      console.log('Profile response error details:', error.response?.data);
      // Try alternative endpoint or method
      try {
        const altResponse = await adminAPI.getCandidates(1, 1, selectedCandidate.email);
        const candidate = altResponse.data.candidates?.[0] || altResponse.data?.[0];
        if (candidate) {
          let skills = [];
          if (candidate.skills) {
            if (Array.isArray(candidate.skills)) {
              skills = candidate.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
            } else if (typeof candidate.skills === 'string') {
              skills = candidate.skills.split(',').map(s => s.trim()).filter(s => s);
            }
          }
          // Also check resumeExtractedData in candidate object
          if (candidate.resumeExtractedData?.skills) {
            const extractedData = candidate.resumeExtractedData;
            let extractedSkills = [];
            if (Array.isArray(extractedData.skills)) {
              extractedSkills = extractedData.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
            } else if (typeof extractedData.skills === 'string') {
              extractedSkills = extractedData.skills.split(',').map(s => s.trim()).filter(s => s);
            }
            extractedSkills.forEach(skill => {
              if (!skills.some(s => s.toLowerCase() === skill.toLowerCase())) {
                skills.push(skill);
              }
            });
          }
          setCandidateSkills(skills);
        } else {
          setCandidateSkills([]);
        }
      } catch (altError) {
        console.error('Alternative fetch also failed:', altError);
        setCandidateSkills([]);
      }
    } finally {
      setLoadingSkills(false);
    }
  };

  // Get non-assigned skills (available skills that candidate doesn't have)
  const getNonAssignedSkills = () => {
    // Normalize candidate skills for comparison (trim, lowercase)
    const candidateSkillsNormalized = candidateSkills.map(s => {
      const skillStr = typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s);
      return skillStr.toLowerCase().trim();
    });
    
    console.log('Candidate skills normalized:', candidateSkillsNormalized);
    console.log('Available skills:', availableSkills);
    
    return availableSkills.filter(skill => {
      const skillName = typeof skill === 'string' ? skill : skill.name || skill.skillName || JSON.stringify(skill);
      const skillNormalized = skillName.toLowerCase().trim();
      const isNotAssigned = !candidateSkillsNormalized.includes(skillNormalized);
      console.log(`Skill "${skillName}" - normalized: "${skillNormalized}", isNotAssigned: ${isNotAssigned}`);
      return isNotAssigned;
    });
  };

  // Get skills grouped by category, with "Others" category first
  const getSkillsByCategory = () => {
    const nonAssignedSkills = getNonAssignedSkills();
    const grouped = {};
    
    nonAssignedSkills.forEach(skill => {
      const skillName = typeof skill === 'string' ? skill : skill.name || skill.skillName || JSON.stringify(skill);
      const category = (typeof skill === 'object' && skill.category) ? skill.category : 'Uncategorized';
      
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(skill);
    });
    
    // Sort categories: "Others" first, then alphabetically
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Others') return -1;
      if (b === 'Others') return 1;
      return a.localeCompare(b);
    });
    
    return { grouped, sortedCategories };
  };

  // Get filtered non-assigned skills (exclude already selected ones from dropdown)
  const getFilteredNonAssignedSkills = () => {
    return getNonAssignedSkills();
  };

  // Check which candidates already have the selected skill
  const checkCandidatesWithSkill = async (skillName) => {
    if (!skillName || candidates.length === 0) {
      setSelectedCandidates([]);
      return;
    }

    try {
      const candidatesWithSkill = [];
      
      // Check each candidate's skills
      for (const candidate of candidates) {
        try {
          const profileResponse = await adminAPI.getCandidateProfile(candidate.id);
          const profile = profileResponse.data;
          
          // Extract existing skills
          let existingSkills = [];
          if (profile.skills) {
            if (Array.isArray(profile.skills)) {
              existingSkills = profile.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
            } else if (typeof profile.skills === 'string') {
              existingSkills = profile.skills.split(',').map(s => s.trim()).filter(s => s);
            }
          }
          
          // Check if candidate has this skill (case-insensitive)
          const hasSkill = existingSkills.some(s => 
            s.toLowerCase().trim() === skillName.toLowerCase().trim()
          );
          
          if (hasSkill) {
            candidatesWithSkill.push(candidate.id);
          }
        } catch (error) {
          console.error(`Error checking skills for candidate ${candidate.id}:`, error);
        }
      }
      
      // Auto-select candidates who already have this skill
      setSelectedCandidates(candidatesWithSkill);
    } catch (error) {
      console.error('Error checking candidates with skill:', error);
      setSelectedCandidates([]);
    }
  };

  const handleSaveSkills = async () => {
    if (!selectedSkill) {
      toast.error('Please select a skill to assign');
      return;
    }

    if (selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    try {
      setSaving(true);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Assign skill to each selected candidate
      for (const candidateId of selectedCandidates) {
        try {
          // Get candidate's current skills
          const profileResponse = await adminAPI.getCandidateProfile(candidateId);
          const profile = profileResponse.data;
          
          // Extract existing skills
          let existingSkills = [];
          if (profile.skills) {
            if (Array.isArray(profile.skills)) {
              existingSkills = profile.skills.map(s => typeof s === 'string' ? s : s.name || s.skillName || JSON.stringify(s));
            } else if (typeof profile.skills === 'string') {
              existingSkills = profile.skills.split(',').map(s => s.trim()).filter(s => s);
            }
          }
          
          // Add selected skill if not already present (avoid duplicates)
          const skillName = selectedSkill;
          if (!existingSkills.some(s => s.toLowerCase() === skillName.toLowerCase())) {
            existingSkills.push(skillName);
          }
          
          // Update candidate skills
          try {
            await adminAPI.updateCandidateSkills(candidateId, existingSkills);
            successCount++;
          } catch (apiError) {
            // Fallback: try updating the full profile
            const updatedProfile = { ...profile, skills: existingSkills };
            await adminAPI.updateCandidateProfile(candidateId, updatedProfile);
            successCount++;
          }
        } catch (error) {
          console.error(`Error assigning skill to candidate ${candidateId}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Skill "${selectedSkill}" assigned successfully to ${successCount} candidate(s). They will now have assessments for this skill.`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to assign skill to ${errorCount} candidate(s).`);
      }
      
      // Reset selections
      setSelectedSkill('');
      setSelectedCandidates([]);
      setSelectedCandidate(null);
      
      fetchCandidates(); // Refresh candidates list
    } catch (error) {
      console.error('Error saving skills:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign skill');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setSelectedSkill('');
  };

  const handleToggleCandidateSelection = (candidateId) => {
    if (selectedCandidates.includes(candidateId)) {
      setSelectedCandidates(selectedCandidates.filter(id => id !== candidateId));
    } else {
      setSelectedCandidates([...selectedCandidates, candidateId]);
    }
  };

  const handleSelectAllCandidates = () => {
    if (selectedCandidates.length === candidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(candidates.map(c => c.id));
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredNonAssignedSkills = getFilteredNonAssignedSkills();
  const { grouped: skillsByCategory, sortedCategories } = getSkillsByCategory();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-secondary-900">Assign Skills to Candidates</h2>
        <p className="text-sm text-gray-600">Assign skills to enable assessments for candidates</p>
      </div>

      {/* Skills Assignment - Single Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Assign "Others" Category Skills to Candidates
                </h3>
                <p className="text-sm text-gray-600 mt-1">Select skills and candidates to assign</p>
              </div>
              {/* Search Candidates */}
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full text-sm"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 space-y-6">
            {/* Single select for Others Category Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select "Others" Category Skill *
              </label>
              {loadingSkills ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <>
                  <select
                    value={selectedSkill}
                    onChange={(e) => {
                      setSelectedSkill(e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  >
                    <option value="">-- Select a skill --</option>
                    {getOthersCategorySkills().map((skill, index) => {
                      const skillName = typeof skill === 'string' ? skill : (skill.name || skill.skillName || JSON.stringify(skill));
                      return (
                        <option key={index} value={skillName}>
                          {skillName}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    {getOthersCategorySkills().length} skill(s) available in "Others" category.
                    {selectedSkill && ' Candidates who already have this skill will be automatically checked.'}
                  </p>
                  {selectedSkill && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Selected Skill:</p>
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {selectedSkill}
                        <button
                          onClick={() => {
                            setSelectedSkill('');
                            setSelectedCandidates([]);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Clear selection"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Candidates Selection */}
            {selectedSkill && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Candidates to Assign Skill *
                  </label>
                  <button
                    onClick={handleSelectAllCandidates}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {selectedCandidates.length === candidates.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No candidates found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {candidates.map((candidate) => (
                        <label
                          key={candidate.id}
                          className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCandidates.includes(candidate.id)}
                            onChange={() => handleToggleCandidateSelection(candidate.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <p className="font-medium text-gray-900">
                              {candidate.firstName} {candidate.lastName}
                            </p>
                            <p className="text-sm text-gray-600">{candidate.email}</p>
                            {candidate.title && (
                              <p className="text-xs text-gray-500 mt-1">{candidate.title}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedCandidates.length} candidate(s) selected
                  {selectedCandidates.length > 0 && ' (Candidates who already have this skill are automatically checked)'}
                </p>
              </div>
            )}

            {/* Assign Button */}
            {selectedSkill && selectedCandidates.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={handleSaveSkills}
                  disabled={saving}
                  className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {saving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Assigning Skill...
                    </span>
                  ) : (
                    `Assign "${selectedSkill}" to ${selectedCandidates.length} Candidate${selectedCandidates.length !== 1 ? 's' : ''}`
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Assigning this skill will enable assessments for the selected candidates
                </p>
              </div>
            )}

            {!selectedSkill && (
              <div className="text-center py-8 text-gray-500">
                <p>Please select a skill from "Others" category to begin</p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default CandidateSkillAssignment;
